/**
 * Whether the visitor asked for less motion.
 *
 * Two sources, either of which is enough: the operating system's
 * `prefers-reduced-motion: reduce`, and the `html[data-motion="reduce"]`
 * hook the development review page and the tests set. The stylesheet stops
 * every animation for both; this is the same answer for the few things the
 * page decides in script — the example clip's autoplay and the CTA's
 * one-shot pixel wave.
 */

import { useEffect, useState } from 'react';

export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

export function reduceMotionRequested(): boolean {
  if (typeof document === 'undefined') return false;
  if (document.documentElement.dataset['motion'] === 'reduce') return true;
  return typeof matchMedia === 'function' && matchMedia(REDUCED_MOTION_QUERY).matches;
}

/**
 * The current answer, kept current: follows the media query as it changes
 * and the `data-motion` hook as it is toggled.
 */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(reduceMotionRequested);

  useEffect(() => {
    const update = () => {
      setReduced(reduceMotionRequested());
    };
    update();
    const query =
      typeof matchMedia === 'function' ? matchMedia(REDUCED_MOTION_QUERY) : null;
    query?.addEventListener('change', update);
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-motion'],
    });
    return () => {
      query?.removeEventListener('change', update);
      observer.disconnect();
    };
  }, []);

  return reduced;
}
